import { contractTypeLabel } from "@/lib/contractLabels";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link, useLocation } from "wouter";
import { Pencil } from "lucide-react";
import type { ContractListItem } from "../../../../server/shared/contracts/contract-queries";

const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador",
  awarded: "Adjudicado",
  pending_signature: "Pendiente firma",
  signed: "Firmado",
  active: "Activo",
  completed: "Completado",
  cancelled: "Cancelado",
};

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  // Preserve the existing UTC calendar day; only localize its presentation.
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });
}

export function ContractsTab({ customerPartnerId }: { customerPartnerId: number }) {
  const [, setLocation] = useLocation();
  const query = trpc.customers.contracts.listForCustomer.useQuery({ customerPartnerId }, { enabled: !!customerPartnerId });

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          No se pudieron cargar los contratos del cliente.
        </CardContent>
      </Card>
    );
  }

  const contracts = (query.data ?? []) as ContractListItem[];

  if (contracts.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Este cliente no tiene contratos todavía.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {contracts.map((c) => (
        <Card
          key={c.id}
          className="cursor-pointer transition-colors hover:border-primary/40"
          onClick={() => setLocation(`/customers/${customerPartnerId}/contracts/${c.id}`)}
        >
          <CardHeader className="min-w-0 pb-2">
            <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
              <CardTitle className="min-w-0 max-w-full text-base font-semibold [overflow-wrap:anywhere]">
                {c.contractCode} — {c.title}
              </CardTitle>
              <Badge variant={c.status === "cancelled" ? "destructive" : c.status === "completed" ? "secondary" : "default"}>
                {STATUS_LABELS[c.status] ?? "Estado registrado"}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">Creación: {formatDate(c.createdAt)}</p>
          </CardHeader>
          <CardContent className="grid gap-1 text-sm sm:grid-cols-2">
            <span>Tipo: {contractTypeLabel(c.contractType)}</span>
            {c.salesOwnerName ? <span>Comercial: {c.salesOwnerName}</span> : null}
            {c.projectManagerName ? <span>Project Manager: {c.projectManagerName}</span> : null}
            <span>Instalaciones: {c.installationCount ?? 0}</span>
            {c.economics?.originalContractAmount != null ? (
              <span>
                Importe: {Number(c.economics.originalContractAmount).toLocaleString("es-ES", { style: "currency", currency: c.economics.currency ?? "EUR" })}
              </span>
            ) : null}
          </CardContent>
          <CardContent className="pt-0">
            <div className="flex items-center gap-3">
              <Button asChild variant="ghost" size="sm" className="px-0">
                <Link
                  href={`/customers/${customerPartnerId}/contracts/${c.id}`}
                  aria-label={`Ver contrato ${c.contractCode}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  Ver contrato →
                </Link>
              </Button>
              {c.status === "draft" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="px-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setLocation(`/customers/${customerPartnerId}/contracts/${c.id}?edit=1`);
                  }}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar contrato
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
